import { Tooltip } from "@mantine/core";
import { PropsWithChildren, useMemo } from "react";

import { ConfirmationPopover } from "@/components/ui/confirmation-popover";
import {
  AssessmentQuestionFields,
  type GenerateButtonState,
} from "@/types/screener-builder";

type MinimalGenerateButtonState = Omit<
  GenerateButtonState<never>,
  "generateInput"
>;

interface GenerateTooltipProps {
  close: () => void;
  field: AssessmentQuestionFields | string;
  isOpen: boolean;
  generateButtonState: MinimalGenerateButtonState;
  loading: boolean;
  onGenerate: () => void;
}

export const GenerateTooltip = ({
  children,
  close,
  field,
  isOpen,
  generateButtonState: {
    disabled: generateButtonDisabled,
    isRegenerate,
    tooltip,
  },
  loading,
  onGenerate,
}: PropsWithChildren<GenerateTooltipProps>) => {
  const regenerateOverwriteWarningField = useMemo(() => {
    switch (field) {
      case AssessmentQuestionFields.Competency:
        return "competency";
      case AssessmentQuestionFields.Definition:
        return "definition";
      case AssessmentQuestionFields.Question:
        return "question(s)";
      default:
        return field;
    }
  }, [field]);

  if (isRegenerate && !generateButtonDisabled) {
    return (
      <ConfirmationPopover
        isOpen={isOpen}
        message={`Are you sure you want to regenerate? Your current ${regenerateOverwriteWarningField} will be replaced.`}
        onClose={close}
        onPrimaryButtonClick={onGenerate}
        primaryButtonContent="Regenerate"
      >
        {children}
      </ConfirmationPopover>
    );
  }

  return (
    <Tooltip disabled={loading || !generateButtonDisabled} label={tooltip}>
      {children}
    </Tooltip>
  );
};
